import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Avatar, Box, Stack, Typography } from '@mui/material';

import dayjs from 'dayjs';

import { VenueNotification, VenueNotificationStatus } from '../../graphql/API';
import { useVenueUpdateNotificationsMutation } from '../../hooks/reactQuery/notifications/useVenueUpdateNotificationsMutation';
import useVenueId from '../../hooks/useVenueId';
import { mapNotificationToRoute } from '../../utils/notifications/route-mapping';

type Props = {
  notification: VenueNotification;
  compact?: boolean;
  onNavigate?: () => void;
};

const NotificationItem: React.FC<Props> = ({ notification, compact = false, onNavigate }) => {
  const navigate = useNavigate();
  const venueID = useVenueId();
  const updateMutation = useVenueUpdateNotificationsMutation({ venueID });

  const isUnread = notification.status === VenueNotificationStatus.sent;

  const relativeTime = React.useMemo(() => {
    if (!notification.createdAt) return '';
    const date = dayjs(notification.createdAt);
    return date.isValid() ? date.fromNow() : '';
  }, [notification.createdAt]);

  const handleClick = React.useCallback(() => {
    if (isUnread) {
      updateMutation.mutate({
        ids: [notification.id],
        status: VenueNotificationStatus.read,
      });
    }

    const route = mapNotificationToRoute(notification);
    if (route) {
      navigate(route);
    }

    onNavigate?.();
  }, [isUnread, notification, updateMutation, navigate, onNavigate]);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        px: compact ? 2 : 3,
        py: compact ? 1.5 : 2,
        cursor: 'pointer',
        backgroundColor: isUnread ? 'rgba(118, 91, 198, 0.06)' : 'transparent',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: isUnread ? 'rgba(118, 91, 198, 0.10)' : 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        {notification.thumbnail ? (
          <Avatar
            src={notification.thumbnail}
            alt=''
            sx={{ width: compact ? 36 : 44, height: compact ? 36 : 44 }}
          />
        ) : (
          <Avatar sx={{ width: compact ? 36 : 44, height: compact ? 36 : 44, bgcolor: '#F2EEFB', color: '#765BC6' }}>
            <NotificationsNoneIcon fontSize='small' />
          </Avatar>
        )}
        {isUnread && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#765BC6',
              border: '2px solid white',
            }}
          />
        )}
      </Box>

      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        {notification.title && (
          <Typography
            variant='body2'
            sx={{
              fontWeight: isUnread ? 600 : 500,
              color: 'rgba(0, 0, 0, 0.87)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {notification.title}
          </Typography>
        )}
        {notification.content && (
          <Typography
            variant='body2'
            sx={{
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: compact ? '0.8125rem' : '0.875rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: compact ? 2 : 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {notification.content}
          </Typography>
        )}
        {relativeTime && (
          <Typography variant='caption' sx={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '0.75rem' }}>
            {relativeTime}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default NotificationItem;
